<script module lang="ts">
  import type { Url } from '#server/types/common'
  import type { AriaRole } from 'svelte/elements'

  const types = {
    success: { iconName: 'check' },
    info: { iconName: 'info-circle' },
    support: { iconName: 'support' },
    warning: { iconName: 'warning' },
    error: { iconName: 'warning' },
  }

  export type FlashType = keyof typeof types

  interface FlashStateCommons {
    role?: AriaRole
    duration?: number
    canBeClosed?: boolean
  }

  interface FlashLink {
    url: Url
    text: string
  }

  export interface FlashStateMessage extends FlashStateCommons {
    type: FlashType
    message?: string
    link?: FlashLink
  }

  interface FlashStateHtml extends FlashStateCommons {
    type: FlashType
    html: string
    link?: FlashLink
  }

  interface LoadingFlash extends FlashStateCommons {
    type: 'loading'
    message?: string
  }

  export type FlashState = FlashStateMessage | FlashStateHtml | LoadingFlash | Error
</script>

<script lang="ts">
  import Link from '#app/lib/components/link.svelte'
  import { icon } from '#app/lib/icons'
  import { logError } from '#app/lib/loggers'
  import { onChange } from '#app/lib/svelte/svelte'
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import Spinner from '#general/components/spinner.svelte'
  import { I18n } from '#user/lib/i18n'
  import type { Snippet } from 'svelte'

  let {
    state: flashState = $bindable(),
    onClose,
    textSnippet,
  }: {
    state?: FlashState
    onClose?: () => void
    textSnippet?: Snippet
  } = $props()

  const type = $derived(flashState instanceof Error ? 'error' : flashState?.type)
  const iconName = $derived(types[type]?.iconName)

  $effect(() => {
    onChange(flashState, () => {
      if (flashState instanceof Error) {
        // Logs the error and report it
        logError(flashState)
        flashState.message ||= I18n('something went wrong :(')
      }
      if (flashState && 'duration' in flashState) {
        const flashStateToRemove = flashState
        setTimeout(() => {
          if (flashState === flashStateToRemove) closeFlash()
        }, flashState.duration)
      }
    })
  })

  function closeFlash (e?: ClickEvent) {
    e?.stopPropagation()
    flashState = null
    onClose?.()
  }
</script>

{#if flashState}
  <div class="flash {type}">
    <div role={'role' in flashState ? flashState.role : (type === 'error' ? 'alert' : 'status')}>
      {#if type === 'loading'}
        <Spinner />
        {'message' in flashState ? flashState.message : I18n('loading')}
      {:else}
        {#if iconName}{@html icon(iconName)}{/if}
        <div class="flash-body">
          {#if textSnippet}
            {@render textSnippet()}
          {:else}
            {#if 'html' in flashState}
              {@html flashState.html}
            {:else if 'message' in flashState}
              {flashState.message}
            {/if}
            {#if 'link' in flashState}
              <Link
                url={flashState.link.url}
                text={flashState.link.text}
                classNames="classic-link"
              />
            {/if}
          {/if}
        </div>
      {/if}
    </div>
    {#if !('canBeClosed' in flashState) || flashState.canBeClosed !== false}
      <button
        onclick={closeFlash}
        title={I18n('close')}
      >
        {@html icon('close')}
      </button>
    {/if}
  </div>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .flash{
    @include display-flex(row, center, space-between);
    padding: 0.5em;
    margin: 0.3em 0;
    padding-inline-start: 0.5em;
    color: $dark-grey;
    overflow-wrap: anywhere;
    @include radius;
    > div{
      // Take all the width, so that a parent's "text-align: center" can be applied
      flex: 1;
      @include display-flex(row, baseline);
    }
    button{
      margin: 0.2em;
      padding: 0;
    }
    :global(a){
      text-decoration: underline;
    }
  }
  .flash-body{
    margin-inline-start: 0.5rem;
  }
  .error{
    background-color: lighten($danger-color, 25%);
  }
  .success{
    background-color: lighten($success-color, 30%);
  }
  .info, .loading{
    background-color: lighten($primary-color, 70%);
  }
  .warning{
    background-color: lighten($yellow, 35%);
  }
</style>
